import { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import SessionDateIcon from "../../../../assets/icons/SessionDateIcon.svg";
import SessionTimeIcon from "../../../../assets/icons/SessionTimeIcon.svg";
import SessionDurationIcon from "../../../../assets/icons/SessionDurationIcon.svg";
import SessionVideoIcon from "../../../../assets/icons/SessionVideoIcon.svg";
import ZoomIcon from "../../../../assets/icons/ZoomIcon.png";
import TeamsIcon from "../../../../assets/icons/TeamsIcon.png";
import ExpertSessionsService from "../../../../services/ExpertSessionsService";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import { Skeleton } from "primereact/skeleton";
import { FaRegCopy } from "react-icons/fa";

const SessionConfirmation = () => {
  const { state } = useLocation();
  const sessionId = state?.sessionId;
  const authContext = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState("");

useEffect(() => {
    if (!sessionId || !authContext?.token) return;

    const controller = new AbortController(); 

    const fetchSessionDetails = async () => {
      setLoading(true);
      try {
        const data = await ExpertSessionsService.GetView(
          sessionId,
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (data?.isSuccess) {
          const d = data.data;
          const startDate = new Date(d.scheduledStartDate + "Z");
          const endDate = new Date(d.scheduledEndDate + "Z");

          const formatTime = (date) =>
            date ? date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "";

          const formatDate = (date) =>
            date ? date.toLocaleDateString("en-GB").replace(/\//g, "-") : "";

          const durationMinutes =
            startDate && endDate
              ? Math.round((endDate - startDate) / 60000)
              : null;

          setSession({
            sessionId: d.id,
            title: d.title,
            expertName: d.instructors?.join(", ") || "—",
            attendeeName: authContext.displayName || "Learner",
            course: d.courseName,
            date: formatDate(startDate),
            time: formatTime(startDate),
            duration: durationMinutes !== null ? `${durationMinutes} minutes` : "—",
            meetingId: d.joinMeetingId || d.meetingId || "—",
            passcode: d.passcode || "—",
            joinUrl: d.meetingUrl || "",
            meetingPlatform: d.meetingPlatform || "",
            instructors: d.instructors || [],
          });
        } else {
          AlertService.error(data?.errorMessage || "Failed to load session");
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        console.error(err);
        AlertService.error("Something went wrong");
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchSessionDetails();

    return () => controller.abort(); 
  }, [sessionId, authContext]);

const handleCopy = async (text, field) => {
  if (!text || text === "—") return;

  try {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);

    setTimeout(() => {
      setCopiedField("");
    }, 1500);
  } catch (err) {
    console.error("Copy failed", err);
  }
};

  const getMeetingIcon = () => {
    const platform = session?.meetingPlatform?.toLowerCase().trim();

    if (platform === "zoom") return ZoomIcon;
    if (platform === "teams") return TeamsIcon;

    return SessionVideoIcon;
  };

  if (loading) {
    return (
      <div className="container pt-4 pt-md-5">
        <div className="mx-auto rounded-4 shadow-sm border-0 p-3 p-md-5 card">
          <div className="text-center mb-4 mt-2">
            <Skeleton width="120px" height="40px" className="mx-auto" />
          </div>
          <div className="text-center mb-3 mt-3">
            <Skeleton width="250px" height="30px" className="mx-auto" />
          </div>

          <div className="text-center mb-4 mb-md-5">
            <Skeleton width="300px" height="20px" className="mx-auto" />
          </div>

          <div className="row">
            <div className="col-md-7 pe-md-5">
              <Skeleton width="150px" height="15px" className="mb-2" />
              <Skeleton width="250px" height="25px" className="mb-4" />

              <div className="d-flex flex-wrap gap-3 gap-md-5 mb-4 mt-3">
                {[1, 2, 3].map((_, i) => (
                  <div key={i} className="d-flex align-items-center gap-2">
                    <Skeleton shape="circle" size="40px" />
                    <div>
                      <Skeleton width="60px" height="12px" className="mb-1" />
                      <Skeleton width="80px" height="15px" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-4 py-3 py-md-4 mb-4 mt-3">
                <div className="d-flex justify-content-center gap-5">
                  <div>
                    <Skeleton width="80px" height="12px" className="mb-1" />
                    <Skeleton width="100px" height="15px" />
                  </div>
                  <div>
                    <Skeleton width="80px" height="12px" className="mb-1" />
                    <Skeleton width="100px" height="15px" />
                  </div>
                </div>
              </div>

              <Skeleton width="90%" height="15px" className="mb-2" />
              <Skeleton width="70%" height="15px" />
            </div>

            <div className="col-md-5 mt-4 mt-md-0">
              <div className="rounded-4 p-3 p-md-4 py-md-5 px-lg-5 text-center mb-3">
                <div className="mb-4 d-flex justify-content-center">
                  <Skeleton shape="circle" size="85px" />
                </div>
                <Skeleton width="90%" height="15px" className="mb-3 mx-auto" />
                <Skeleton width="100%" height="40px" />
              </div>
              <div className="rounded-4 p-3 px-md-5">
                <Skeleton width="100%" height="15px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session)
    return <div className="text-center mt-5">Session not found.</div>;

  return (
    <div className="container pt-4 pt-md-5 pb-3">
      <div className="mx-auto rounded-4 shadow-sm border-0 p-3 p-md-5 card">

        <h2 className="text-center fw-semibold mb-3 mt-2 fs-3 fs-md-4">
          Hello {session.attendeeName},
        </h2>

        <p className="text-center fs-6 mb-4 mb-md-5 px-2">
          You have successfully enrolled in the following live session
        </p>

        <div className="row">
          {/* LEFT SECTION */}
          <div className="col-md-7 pe-md-5">
            <p className="fw-medium mb-2">Meeting Title:</p>
            <h5 className="fw-semibold mb-2 pb-2">{session.title}</h5>
             <p className="fw-medium mb-2">{session?.instructors?.length > 1 ? "Instructors" : "Instructor"}:</p>
    <h6 className="fw-semibold mb-4 pb-2">
    {session?.instructors?.join(", ") || "N/A"}
  </h6>

            {/* ICON ROW */}
            <div className="d-flex flex-wrap align-items-center gap-3 gap-md-5 mb-4 pb-2 mt-3 w-xl-90">
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <img src={SessionDateIcon} alt="" width={40} />
                <div>
                  <small className="d-block">Date</small>
                  <span className="fw-semibold">{session.date}</span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <img src={SessionTimeIcon} alt="" width={40} />
                <div>
                  <small className="d-block">Time</small>
                  <span className="fw-semibold">{session.time}</span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <img src={SessionDurationIcon} alt="" width={40} />
                <div>
                  <small className="d-block">Duration</small>
                  <span className="fw-semibold">{session.duration}</span>
                </div>
              </div>
            </div>

            {/* MEETING BOX */}

            <div className="rounded-4 border-0 py-3 py-md-4 mb-4 mt-3 session-box w-xl-90">
              <div className="d-flex flex-row justify-content-center align-items-center gap-3 gap-md-5 text-center">
<div className="text-start position-relative">
  <small className="d-block pb-1">Meeting ID</small>

  <div className="d-flex align-items-center gap-2">
    <span
      title="Click to copy"
      className="fw-semibold"
      style={{ cursor: "pointer" }}
      onClick={() => handleCopy(session.meetingId, "meetingId")}
    >
      {session.meetingId}
    </span>

    <FaRegCopy
      title="Click to copy"
      style={{ cursor: "pointer", fontSize: 14 }}
      onClick={() => handleCopy(session.meetingId, "meetingId")}
      className="mb-1"
    />
  </div>

  {copiedField === "meetingId" && (
    <span className="copy-tooltip">Copied!</span>
  )}
</div>

<div className="text-start position-relative">
  <small className="d-block pb-1">Passcode</small>

  <div className="d-flex align-items-center gap-2">
    <span
      title="Click to copy"
      className="fw-semibold"
      style={{ cursor: "pointer" }}
      onClick={() => handleCopy(session.passcode, "passcode")}
    >
      {session.passcode}
    </span>

    <FaRegCopy
      title="Click to copy"
      style={{ cursor: "pointer", fontSize: 14 }}
      onClick={() => handleCopy(session.passcode, "passcode")}
      className="mb-1"
    />
  </div>

  {copiedField === "passcode" && (
    <span className="copy-tooltip">Copied!</span>
  )}
</div>

              </div>
            </div>

            <p className="mb-2 w-xl-90">
              If you experience any issues joining the session, please{" "}
              <a
                href="/#/support/add"
                className="fw-medium text-decoration-none link"
              >
                create a ticket for support,
              </a>{" "}
              or send an email to{" "}
              <a
                href="mailto:cyberbaysupport@tevelcyber.com"
                className="fw-medium text-decoration-none link"
              >
                cyberbaysupport@tevelcyber.com
              </a>
            </p>
            <p className="mt-3">
              We look forward to seeing you in the session!
            </p>
          </div>

          {/* RIGHT SECTION */}
          <div className="col-md-5 mt-4 mt-md-0">
            <div
              className="rounded-4 p-3 p-md-4 py-md-5 px-lg-5 text-center mb-3"
              style={{ border: "0.5px solid #2f415642" }}
            >
              <div className="mb-4 d-flex justify-content-center">
                <div className="d-flex align-items-center justify-content-center">
                  <img src={getMeetingIcon()} alt="" width={85} />
                </div>
              </div>
              <p className="mb-3 mb-md-4 px-1 px-md-2">
                Click the link below at the scheduled time to join the session:
              </p>
              <button
                className="btn btn-session text-white w-100 py-2 py-md-3"
                onClick={() => {
                  if (!session.joinUrl) {
                    AlertService.error("Meeting link not available");
                    return;
                  }
                  window.open(session.joinUrl, "_blank");
                }}
              >
                Join the Meeting
              </button>
            </div>

            <div className="rounded-4 p-3 px-md-5 session-box text-center text-md-start">
              We recommend joining the meeting 5 minutes early to ensure
              your audio and video are working properly.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionConfirmation;
